<template>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="space-y-8">
            <!-- Header -->
            <div class="flex justify-between items-center">
                <div>
                    <h1 class="text-3xl font-bold text-gray-900 dark:text-white">All Games</h1>
                    <p class="mt-2 text-gray-600 dark:text-gray-400">Complete game history</p>
                </div>
                
                <!-- Filters -->
                <div class="flex items-center space-x-6">
                    <div class="flex items-center space-x-2">
                        <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Sort:</label>
                        <select v-model="sortOrder" class="input">
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                        </select>
                    </div>
                    <div class="flex items-center space-x-2">
                        <label for="year-select" class="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Filter by year:
                        </label>
                        <select
                            id="year-select"
                            v-model="selectedYear"
                            @change="fetchGames"
                            class="input"
                        >
                            <option value="">All Years</option>
                            <option v-for="year in availableYears" :key="year" :value="year">
                                {{ year }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>

            <!-- Error State -->
            <div v-if="error" class="p-4 bg-error-50 border border-error-200 rounded-md">
                <p class="text-error-600">{{ error }}</p>
            </div>

            <!-- Games Content -->
            <div v-else class="space-y-8">
                <!-- Season Winner Card (when year is selected and < current year) -->
                <div v-if="shouldShowSeasonWinner && seasonWinner" class="card">
                    <div class="card-body text-center py-4">
                        <h2 class="text-lg font-medium text-gray-900 dark:text-white">🏆 Season Winner: {{ seasonWinner }} 🏆</h2>
                    </div>
                </div>

                <!-- Season Stats (when year is selected) or All Time Stats -->
                <div v-if="overview" class="card">
                    <div class="card-header">
                        <h2 class="text-lg font-medium">{{ selectedYear ? `${selectedYear} Season Stats - ${totalGameCount} Games` : `All Time Stats - ${totalGameCount} Games` }}</h2>
                    </div>
                    <div class="card-body">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div v-for="(player, username) in overview.players" :key="username">
                                <h3 class="font-medium text-gray-900 dark:text-white mb-3">{{ username }}</h3>
                                <div class="grid grid-cols-3 gap-4">
                                    <div class="stat-card">
                                        <div class="stat-value">{{ player.wins || 0 }}</div>
                                        <div class="stat-label">Wins</div>
                                    </div>
                                    <div class="stat-card">
                                        <div class="stat-value">{{ player.losses || 0 }}</div>
                                        <div class="stat-label">Losses</div>
                                    </div>
                                    <div class="stat-card">
                                        <div class="stat-value">{{ player.goals || 0 }}</div>
                                        <div class="stat-label">Goals</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Games List -->
                <div class="space-y-4">
                    <div class="flex justify-between items-center">
                        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                            Games {{ selectedYear ? `(${selectedYear})` : '' }}
                        </h2>
                        <div class="text-sm text-gray-600 dark:text-gray-400">
                            {{ games.length }} game{{ games.length !== 1 ? 's' : '' }} found
                        </div>
                    </div>

                    <div v-if="games.length === 0" class="card">
                        <div class="card-body text-center py-12">
                            <p class="text-gray-500 dark:text-gray-400">
                                No games found{{ selectedYear ? ` for ${selectedYear}` : '' }}
                            </p>
                        </div>
                    </div>

                    <div v-else class="space-y-4">
                        <div v-for="game in games" :key="game.id" class="card hover:shadow-md transition-shadow">
                            <div class="card-body">
                                <div class="grid grid-cols-12 gap-4 items-center">
                                    <!-- Left: Game ID and Date -->
                                    <div class="col-span-2 flex items-center space-x-4 pl-2">
                                        <div class="text-2xl font-bold text-gray-400 dark:text-gray-500">
                                            {{ getDisplayGameId(game.id) }}
                                        </div>
                                        <div class="text-center">
                                            <div class="text-base font-bold text-gray-900 dark:text-white">
                                                {{ formatDateShort(game.date || game.created_at) }}
                                            </div>
                                            <div class="text-xs text-gray-500">
                                                {{ formatYear(game.date || game.created_at) }}
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Center: Game matchup -->
                                    <div class="col-span-8 flex items-center justify-center space-x-6">
                                        <!-- Away Team -->
                                        <div class="flex items-center space-x-3 min-w-[200px] justify-end">
                                            <div class="text-center">
                                                <div class="font-semibold text-gray-900 dark:text-white">{{ game.away_username }}</div>
                                                <div class="text-sm text-gray-600 dark:text-gray-300">{{ getTeamFullName(game.away_team) }}</div>
                                                <div class="text-xs text-gray-400">Away</div>
                                            </div>
                                            <img 
                                                :src="getTeamLogoUrl(game.away_team)" 
                                                :alt="`${game.away_team} logo`"
                                                class="w-8 h-8 object-contain"
                                                @error="($event.target as HTMLImageElement).style.display = 'none'"
                                            />
                                        </div>

                                        <!-- Score -->
                                        <div class="flex flex-col items-center">
                                            <div class="flex items-center space-x-4">
                                                <div class="text-3xl font-bold text-gray-900 dark:text-white">{{ game.away_score }}</div>
                                                <div class="text-gray-400 text-xl">—</div>
                                                <div class="text-3xl font-bold text-gray-900 dark:text-white">{{ game.home_score }}</div>
                                            </div>
                                            <!-- OT/SO Indicator centered under score -->
                                            <div v-if="game.is_overtime" class="mt-1">
                                                <span v-if="game.is_shootout" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                    SO
                                                </span>
                                                <span v-else class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                                    OT
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Home Team -->
                                        <div class="flex items-center space-x-3 min-w-[200px] justify-start">
                                            <img 
                                                :src="getTeamLogoUrl(game.home_team)" 
                                                :alt="`${game.home_team} logo`"
                                                class="w-8 h-8 object-contain"
                                                @error="($event.target as HTMLImageElement).style.display = 'none'"
                                            />
                                            <div class="text-center">
                                                <div class="font-semibold text-gray-900 dark:text-white">{{ game.home_username }}</div>
                                                <div class="text-sm text-gray-600 dark:text-gray-300">{{ getTeamFullName(game.home_team) }}</div>
                                                <div class="text-xs text-gray-400">Home</div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Right: Details button -->
                                    <div class="col-span-2 flex items-center justify-end">
                                        <router-link 
                                            :to="`/game/${game.id}`" 
                                            class="btn-secondary text-sm"
                                        >
                                            Details →
                                        </router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { gameApi } from '@/services/api'
import { useNHLTeams } from '@/composables/useNHLTeams'
import { useTeamLogos } from '@/composables/useTeamLogos'

interface Game {
    id: number
    date: string
    created_at: string
    home_username: string
    away_username: string
    winner_username?: string
    home_team: string
    away_team: string
    home_score: number
    away_score: number
    is_overtime?: boolean
    is_shootout?: boolean
}

interface Player {
    username: string
    games_played: number
    wins: number
    losses: number
    goals: number
    shots: number
    hits: number
    currentStreak: string
}

interface Overview {
    players: Record<string, Player>
    totalGames: number
    availableYears: number[]
}

const error = ref<string | null>(null)
const rawGames = ref<Game[]>([])
const overview = ref<Overview | null>(null)
const availableYears = ref<number[]>([])
const selectedYear = ref<string>('')
const sortOrder = ref('newest')
const isInitialLoad = ref(true)

// Composables
const { getTeamFullName } = useNHLTeams()
const { getTeamLogoUrl } = useTeamLogos()

// Computed property that automatically sorts games when sortOrder or rawGames changes
const games = computed(() => {
    if (!rawGames.value || rawGames.value.length === 0) return []
    
    return [...rawGames.value].sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        
        return sortOrder.value === 'newest' ? dateB - dateA : dateA - dateB
    })
})

// Computed property for total game count
const totalGameCount = computed(() => {
    if (!rawGames.value || rawGames.value.length === 0) {
        return overview.value?.totalGames || 0
    }
    return rawGames.value.length
})

// Computed property to determine if season winner should be shown
const shouldShowSeasonWinner = computed(() => {
    if (!selectedYear.value || !overview.value) return false
    const currentYear = new Date().getFullYear()
    const selectedYearNum = parseInt(selectedYear.value)
    return selectedYearNum < currentYear
})

// Computed property to calculate season winner
const seasonWinner = computed(() => {
    if (!overview.value || !shouldShowSeasonWinner.value) return null
    
    let maxWins = 0
    let winner = null
    
    for (const [username, player] of Object.entries(overview.value.players)) {
        const wins = player.wins || 0
        if (wins > maxWins) {
            maxWins = wins
            winner = username
        }
    }
    
    return winner
})

const fetchGames = async () => {
    try {
        error.value = null

        const year = selectedYear.value ? parseInt(selectedYear.value) : undefined
        
        const [gamesResponse, overviewResponse] = await Promise.all([
            gameApi.getGames(year),
            gameApi.getOverview(year)
        ])

        rawGames.value = gamesResponse.data.games
        overview.value = overviewResponse.data
        
        if (!selectedYear.value) {
            availableYears.value = overviewResponse.data.availableYears || []
            
            // Set default to most recent year on initial load only
            if (isInitialLoad.value && availableYears.value.length > 0) {
                selectedYear.value = availableYears.value[0].toString()
                isInitialLoad.value = false
                // Fetch games again with the selected year
                await fetchGames()
                return
            }
        }
        
        isInitialLoad.value = false

    } catch (err: any) {
        error.value = err.response?.data?.error || 'Failed to load games'
    }
}


const formatDateShort = (dateString: string) => {
    if (!dateString) return 'Unknown';
    
    // Handle both YYYY-MM-DD format and ISO timestamp format
    let date: Date;
    if (dateString.length === 10 && dateString.includes('-')) {
        // YYYY-MM-DD format - parse as UTC to avoid timezone shifts
        date = new Date(dateString + 'T00:00:00.000Z');
    } else {
        // ISO timestamp format
        date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }
    
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
    });
}

const formatYear = (dateString: string) => {
    if (!dateString) return 'Unknown';
    
    // Handle both YYYY-MM-DD format and ISO timestamp format
    let date: Date;
    if (dateString.length === 10 && dateString.includes('-')) {
        // YYYY-MM-DD format - parse as UTC to avoid timezone shifts
        date = new Date(dateString + 'T00:00:00.000Z');
    } else {
        // ISO timestamp format
        date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
        return 'Unknown';
    }
    
    return date.getUTCFullYear().toString();
}

const getDisplayGameId = (gameId: number) => {
    return gameId
}

onMounted(() => {
    fetchGames()
})
</script>